import { TEMPLATE_NAMES } from "../templates/TemplateRenderer.js"
import { joinListBlocks, splitListBlocks } from "./listBlockFormat.js"

interface PreviewRow {
  previewTargetId: string;
  label: string;
  editKind: string;
  dataKey?: string;
}

export const PREVIEW_PAGE1_ROWS: PreviewRow[] = [
  { previewTargetId: "title", label: "Title", editKind: "flat", dataKey: "title" },
  { previewTargetId: "subtitle", label: "Subtitle", editKind: "flat", dataKey: "subtitle" },
  { previewTargetId: "at_a_glance", label: "At a Glance", editKind: "flat", dataKey: "at_a_glance" },
  { previewTargetId: "key_metrics", label: "Key Metrics", editKind: "key_metrics" },
  { previewTargetId: "challenges", label: "Challenges", editKind: "challenges", dataKey: "challenges" },
  { previewTargetId: "objectives", label: "Objectives", editKind: "objectives" },
  { previewTargetId: "outcomes", label: "Outcomes", editKind: "outcomes" },
]

export const PREVIEW_PAGE2_ROWS: PreviewRow[] = [
  {
    previewTargetId: "partnership_client_overview",
    label: "Client overview",
    editKind: "partnership_client_overview",
  },
  {
    previewTargetId: "partnership_bullets",
    label: "Partnership bullets",
    editKind: "partnership_bullets",
  },
  { previewTargetId: "call_to_action", label: "Call to action", editKind: "call_to_action" },
]

export function getPreviewRowsForTemplate(templateName: string): PreviewRow[] {
  return templateName === TEMPLATE_NAMES.PAGE_2 ? PREVIEW_PAGE2_ROWS : PREVIEW_PAGE1_ROWS
}

export function previewRowCanEdit(row: PreviewRow, restrictedQuick?: Set<string>): boolean {
  const key = row.dataKey || row.previewTargetId
  if (restrictedQuick?.has(key)) return false
  return true
}

export function previewRowExistsInData(row: PreviewRow, data: Record<string, any>): boolean {
  if (!data) return false
  switch (row.editKind) {
    case "flat": {
      const k = row.dataKey || row.previewTargetId
      const v = data[k]
      if (v == null) return false
      if (typeof v === "string") return v.trim() !== ""
      return false
    }
    case "challenges": {
      const arr = data.challenges
      return Array.isArray(arr) && arr.length > 0 && arr.some((x: unknown) => String(x).trim())
    }
    case "objectives": {
      const arr = data.objectives
      return Array.isArray(arr) && arr.length > 0 && arr.some((x: unknown) => String(x).trim())
    }
    case "key_metrics": {
      const arr = data.key_metrics
      return Array.isArray(arr) && arr.length > 0 && arr.some((m: any) => String(m?.title || m?.description || "").trim())
    }
    case "outcomes": {
      const arr = data.outcomes
      return Array.isArray(arr) && arr.length > 0 && arr.some((o: any) => String(o?.title || o?.description || "").trim())
    }
    case "partnership_client_overview": {
      const pd = data.partnership_details
      if (!pd || typeof pd !== "object") return false
      return String(pd.client_overview || "").trim() !== ""
    }
    case "partnership_bullets": {
      const pd = data.partnership_details
      if (!pd || typeof pd !== "object") return false
      const bullets = pd.partnership_details
      return (
        Array.isArray(bullets) &&
        bullets.length > 0 &&
        bullets.some((b: any) => String(b?.title || "").trim() || String(b?.description || "").trim())
      )
    }
    case "call_to_action": {
      const c = data.call_to_action
      if (!c || typeof c !== "object") return false
      return String(c.title || "").trim() !== "" || String(c.description || "").trim() !== ""
    }
    default:
      return false
  }
}

export function serializePartnershipBullets(bullets: any[]): string {
  if (!Array.isArray(bullets)) return ""
  return bullets
    .map((b) => {
      const t = (b?.title || "").trim()
      const d = (b?.description || "").trim()
      if (!t && !d) return ""
      return `### ${t}\n${d}`
    })
    .filter(Boolean)
    .join("\n\n")
}

interface PartnershipBullet {
  title: string;
  description: string;
}

export function parsePartnershipBulletBlocks(text: string): PartnershipBullet[] {
  const sections = text.split(/(?=### )/).filter((s) => s.trim())
  const out: PartnershipBullet[] = []
  for (const section of sections) {
    const trimmed = section.trim()
    if (!trimmed.startsWith("### ")) continue
    const lines = trimmed.split("\n")
    const title = lines[0].replace(/^###\s+/, "").trim()
    const description = lines.slice(1).join("\n").trim()
    if (title || description) out.push({ title, description })
  }
  return out
}

export function getPreviewModalInitialText(row: PreviewRow, data: Record<string, any>): string {
  switch (row.editKind) {
    case "flat": {
      const k = row.dataKey || row.previewTargetId
      const v = data[k]
      return typeof v === "string" ? v.replace(/^"(.*)"$/, "$1") : ""
    }
    case "challenges":
      return joinListBlocks(data.challenges)
    case "objectives":
      return joinListBlocks(data.objectives)
    case "key_metrics": {
      const arr = Array.isArray(data.key_metrics) ? data.key_metrics : []
      return arr.map((m: any) => {
        const t = String(m?.title || "").trim()
        const d = String(m?.description || "").trim()
        return `### ${t}\n${d}`
      }).join("\n\n")
    }
    case "outcomes": {
      const arr = Array.isArray(data.outcomes) ? data.outcomes : []
      return arr.map((o: any) => {
        const t = String(o?.title || "").trim()
        const d = String(o?.description || "").trim()
        return `### ${t}\n${d}`
      }).join("\n\n")
    }
    case "partnership_client_overview":
      return String(data.partnership_details?.client_overview || "")
    case "partnership_bullets":
      return serializePartnershipBullets(data.partnership_details?.partnership_details || [])
    case "call_to_action": {
      const c = data.call_to_action || {}
      return String(c.description || "").trim()
    }
    default:
      return ""
  }
}

export function applyPartnershipBulletsDescriptionsSave(caseStudyWrapper: { data?: Record<string, any> }, descriptions: string[]): Record<string, any> {
  const data = { ...(caseStudyWrapper?.data || {}) }
  const prev =
    data.partnership_details && typeof data.partnership_details === "object"
      ? data.partnership_details
      : {}
  const prevBullets = Array.isArray(prev.partnership_details) ? prev.partnership_details : []
  const nextBullets = prevBullets.map((b: any, i: number) => ({
    ...b,
    title: String(b?.title ?? "").trim(),
    description: typeof descriptions[i] === "string" ? descriptions[i] : String(descriptions[i] ?? ""),
  }))
  data.partnership_details = {
    ...prev,
    client_overview: prev.client_overview ?? "",
    partnership_details: nextBullets,
  }
  return data
}

export function applyPreviewModalSave(row: PreviewRow, text: string, caseStudyWrapper: { data?: Record<string, any> }): Record<string, any> {
  const data = { ...(caseStudyWrapper?.data || {}) }
  switch (row.editKind) {
    case "flat": {
      const k = row.dataKey || row.previewTargetId
      data[k] = text
      break
    }
    case "challenges":
      data.challenges = splitListBlocks(text)
      break
    case "objectives":
      data.objectives = splitListBlocks(text)
      break
    case "key_metrics": {
      const blocks = parsePartnershipBulletBlocks(text)
      data.key_metrics = blocks.map((b: any) => ({ title: b.title || "", description: b.description || "" }))
      break
    }
    case "outcomes": {
      const blocks = parsePartnershipBulletBlocks(text)
      data.outcomes = blocks.map((b: any) => ({ title: b.title || "", description: b.description || "" }))
      break
    }
    case "partnership_client_overview": {
      const prev = data.partnership_details && typeof data.partnership_details === "object" ? data.partnership_details : {}
      data.partnership_details = {
        ...prev,
        client_overview: text,
        partnership_details: Array.isArray(prev.partnership_details) ? prev.partnership_details : [],
      }
      break
    }
    case "partnership_bullets": {
      const prev = data.partnership_details && typeof data.partnership_details === "object" ? data.partnership_details : {}
      const bullets = parsePartnershipBulletBlocks(text)
      data.partnership_details = {
        ...prev,
        client_overview: prev.client_overview ?? "",
        partnership_details: bullets.length > 0 ? bullets : prev.partnership_details || [],
      }
      break
    }
    case "call_to_action": {
      const prev = data.call_to_action && typeof data.call_to_action === "object" ? data.call_to_action : {}
      data.call_to_action = {
        ...prev,
        title: prev.title || "Ready to Transform Your Security Posture?",
        description: text.trim(),
      }
      break
    }
    default:
      break
  }
  return data
}

export function findPreviewRowByTargetId(previewTargetId: string, templateName: string): PreviewRow | undefined {
  return getPreviewRowsForTemplate(templateName).find((r) => r.previewTargetId === previewTargetId)
}

export function isBlockEditKind(editKind: string): boolean {
  return editKind === "key_metrics" || editKind === "outcomes" || editKind === "call_to_action" || editKind === "partnership_bullets" || editKind === "objectives" || editKind === "challenges"
}

export function getBlocksForEditKind(editKind: string, data: Record<string, any>): { title: string; description: string }[] {
  switch (editKind) {
    case "key_metrics":
      return (Array.isArray(data.key_metrics) ? data.key_metrics : []).map((m: any) => ({
        title: String(m?.title || ""),
        description: String(m?.description || ""),
      }))
    case "outcomes":
      return (Array.isArray(data.outcomes) ? data.outcomes : []).map((o: any) => ({
        title: String(o?.title || ""),
        description: String(o?.description || ""),
      }))
    case "objectives":
      return (Array.isArray(data.objectives) ? data.objectives : []).map((o: any) => ({
        title: "",
        description: String(typeof o === "string" ? o : o?.title || o?.description || ""),
      }))
    case "challenges":
      return (Array.isArray(data.challenges) ? data.challenges : []).map((c: any) => ({
        title: "",
        description: String(typeof c === "string" ? c : c?.title || c?.description || ""),
      }))
    case "call_to_action": {
      const c = data.call_to_action || {}
      return [{ title: String(c.title || ""), description: String(c.description || "") }]
    }
    case "partnership_bullets":
      return (Array.isArray(data.partnership_details?.partnership_details) ? data.partnership_details.partnership_details : []).map((b: any) => ({
        title: String(b?.title || ""),
        description: String(b?.description || ""),
      }))
    default:
      return []
  }
}

export function applyBlocksSave(editKind: string, blocks: { title: string; description: string }[], data: Record<string, any>): Record<string, any> {
  const next = { ...data }
  switch (editKind) {
    case "key_metrics":
      next.key_metrics = blocks.map((b) => ({ title: b.title, description: b.description }))
      break
    case "outcomes":
      next.outcomes = blocks.map((b) => ({ title: b.title, description: b.description }))
      break
    case "objectives":
      next.objectives = blocks.map((b) => b.description)
      break
    case "challenges":
      next.challenges = blocks.map((b) => b.description)
      break
    case "call_to_action":
      next.call_to_action = { title: blocks[0]?.title || "", description: blocks[0]?.description || "" }
      break
    case "partnership_bullets": {
      const prev = data.partnership_details && typeof data.partnership_details === "object" ? data.partnership_details : {}
      next.partnership_details = {
        ...prev,
        client_overview: prev.client_overview ?? "",
        partnership_details: blocks.map((b) => ({ title: b.title, description: b.description })),
      }
      break
    }
  }
  return next
}
